import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { config, bcUrl } from "./config";
import { signOut } from "./auth";
import { Icon } from "./icons";
import { Mark } from "./ui";
import { SUITE, areaForPath, areaNav, areaHref, type Domain, type Page } from "./suite";
import { AreaGrid } from "./SuiteGrid";
import { TopSearch, openPage } from "./TopSearch";
import { findPages } from "./findPages";
import { CourageChat } from "./CourageChat";
import { canOpen, markLanded, startArea, startViewAs, stopViewAs, useAccess, type SecMe } from "./security/store";
import { request } from "./api";
import { myWork, waitingByArea, type Counts } from "./mywork/myWorkApi";
import "./security/security.css";
import "./mywork/mywork.css";

/** Across the top while an administrator is viewing as someone: who, what they can do, and the way back. */
function ViewAsBanner({ m }: { m: SecMe }) {
  if (!m.viewAs) return null;
  const v = m.viewAs;
  return (
    <div className="va-banner" role="status">
      <span className="eye" aria-hidden="true">{v.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</span>
      <span>Viewing as <b>{v.name}</b>{m.person.title ? <span className="meta"> · {m.person.title}</span> : null}</span>
      <span className="meta">{m.roles.join(", ") === m.person.title ? "" : `${m.roles.length ? m.roles.join(", ") : "No role"}${m.implicitDefault ? " (default)" : ""} · `}{v.readOnly ? "look only" : "acting as them"}</span>
      <span className="sp" />
      <a href={`/settings/security/check?user=${encodeURIComponent(v.id)}`} onClick={(e) => { e.preventDefault(); stopViewAs(`/settings/security/check?user=${encodeURIComponent(v.id)}`); }}>Check their access</a>
      <button className="stop" onClick={() => stopViewAs()}>Stop viewing as</button>
    </div>
  );
}

/** For administrators: pick anyone and see courageMFG exactly as they do. */
function ViewAsPicker({ onClose }: { onClose: () => void }) {
  const [people, setPeople] = useState<{ id: string; name: string; title: string | null; demo: boolean; roles: { name: string }[] }[] | null>(null);
  useEffect(() => { request<any[]>("GET", "/api/security/people").then(setPeople, () => setPeople([])); }, []);
  const ref = useDismiss<HTMLDivElement>(onClose, ".va-toggle");
  return (
    <div className="va-pick" role="dialog" aria-label="View as" ref={ref}>
      <h3>View as</h3>
      <p>See every app and record exactly as they would. Demo people can be acted as; real people are look only.</p>
      <ul>
        {!people ? <li className="muted" style={{ padding: 8 }}>Loading…</li> : people.map((p) => (
          <li key={p.id}><button onClick={() => { onClose(); startViewAs({ id: p.id, name: p.name }); }}>
            <span><b>{p.name}</b>{p.demo && <span className="pill warn sm" style={{ marginLeft: 6 }}>Demo</span>}<br /><span className="muted" style={{ fontSize: 12 }}>{p.title ?? ""}</span></span>
            <span className="r">{p.roles.map((r) => r.name).join(", ") || "No role"}</span>
          </button></li>))}
      </ul>
      <div className="va-foot"><Link to="/settings/security" onClick={onClose}>Security settings</Link></div>
    </div>
  );
}

/** Closes a popover on Esc or a click outside it (and outside the button that opens it, which toggles it itself). */
function useDismiss<T extends HTMLElement>(onClose: () => void, toggle: string) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const down = (e: MouseEvent) => { const t = e.target as HTMLElement; if (!ref.current?.contains(t) && !t.closest(toggle)) onClose(); };
    document.addEventListener("keydown", key); document.addEventListener("mousedown", down);
    return () => { document.removeEventListener("keydown", key); document.removeEventListener("mousedown", down); };
  }, [onClose, toggle]);
  return ref;
}

/** All apps: every area, with a box that narrows them as you type and lists the pages that match. Enter opens the
 *  first match. */
function Launcher({ onClose, waiting, first }: { onClose: () => void; waiting: Record<string, number>; first: string | null }) {
  const ref = useDismiss<HTMLDivElement>(onClose, ".waffle");
  const nav = useNavigate();
  const access = useAccess();
  const [q, setQ] = useState("");
  const pages = findPages(q, access, 6).filter((p) => p.where !== "Area home");
  const enter = () => {
    const card = ref.current?.querySelector<HTMLAnchorElement>(".area-card");
    if (pages[0] && !card?.textContent?.toLowerCase().startsWith(q.trim().toLowerCase())) { openPage(pages[0], nav); onClose(); }
    else card?.click();
  };
  return (
    <div className="launcher" role="dialog" aria-label="All apps" ref={ref}>
      <div className="launcher-h">
        <strong>All apps</strong>
        <input className="launcher-q" autoFocus aria-label="Find an area or page" placeholder="Find an area or page" value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) { e.preventDefault(); enter(); } }} />
        <button className="icon-btn" aria-label="Close" onClick={onClose}><Icon name="close" size={14} /></button>
      </div>
      {pages.length > 0 && (
        <ul className="launcher-pages" aria-label="Pages">
          {pages.map((p) => (
            <li key={p.key}><button type="button" onClick={() => { openPage(p, nav); onClose(); }}>
              <span className="n">{p.name}{p.bc ? <span className="ts-ext"><Icon name="ext" size={11} /></span> : null}</span><span className="w">{p.where}</span>
            </button></li>
          ))}
        </ul>
      )}
      <AreaGrid onPick={onClose} waiting={waiting} first={first} filter={q} />
      {q.trim() && <p className="launcher-more"><Link to={`/search?q=${encodeURIComponent(q.trim())}`} onClick={onClose}>Search records for “{q.trim()}”</Link></p>}
    </div>
  );
}

/** The navigation row inside an area. It leads with the area itself, which opens the area's home, then the area's
 *  apps, then its pages in Business Central. Courage home and My Work have no row: they're not in an area. */
function AreaNav({ area, open, counts }: { area: Domain; open: (p: Page) => boolean; counts: Record<string, number> }) {
  const home = areaHref(area);
  const pages = areaNav(area).filter((p) => p.name !== "Home" && p.to !== home);
  const inCourage = pages.filter((p) => p.to && open(p));
  const elsewhere = pages.filter((p) => !p.to);
  return (
    <nav className="areanav" aria-label={area.name}>
      <NavLink to={home} end className="anav-area" title={`${area.name} home`}>
        <span className="anav-ico" aria-hidden="true"><Icon name={area.icon} size={13} /></span>{area.name}
      </NavLink>
      {inCourage.map((p) => <NavPage key={p.name} p={p} />)}
      {elsewhere.length > 0 && (
        <span className="anav-ext" role="group" aria-label="Opens in Business Central or another system">
          {elsewhere.some((p) => p.bc) && <span className="anav-ext-l">Business Central</span>}
          {elsewhere.map((p) => <NavPage key={p.name} p={p} />)}
        </span>
      )}
      {area.id === "quality" && counts.issues ? <Link className="anav-count" to="/issues" title="Open the issues list">{counts.issues} new issues</Link> : null}
    </nav>
  );
}

function NavPage({ p }: { p: Page }) {
  if (p.to) return <NavLink to={p.to} end={p.end ?? p.to.startsWith("/a/")} className="anav-item">{p.name}</NavLink>;
  if (p.bc) return <a className="anav-item" href={bcUrl(p.bc)} target="_blank" rel="noopener noreferrer" title={`${p.name} opens in Business Central`}>{p.name}<span className="ext" aria-hidden="true"><Icon name="ext" size={11} /></span></a>;
  if (p.url) return <a className="anav-item" href={p.url} target="_blank" rel="noopener noreferrer">{p.name}</a>;
  return null;
}

export function Shell({ name, counts, children }: { name: string; counts: Record<string, number>; children: ReactNode }) {
  const loc = useLocation();
  const [launcher, setLauncher] = useState(false);
  const [picker, setPicker] = useState(false);
  const access = useAccess();
  const open = (p: Page) => !p.to || canOpen(access, p.to.split("?")[0]);
  const [chatOpen, setChatOpen] = useState(() => window.innerWidth > 1280 && localStorage.getItem("copilot") !== "off");
  // A page can open Courage Chat (a process map does, to ask for a change to the map).
  useEffect(() => { const o = () => setChatOpen(true); window.addEventListener("courage:open-chat", o); return () => window.removeEventListener("courage:open-chat", o); }, []);
  // A visit that starts anywhere but / has already landed: the logo goes to Courage home from then on.
  useEffect(() => { if (loc.pathname !== "/") markLanded(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // My Work's count in the top bar: what's waiting on the person. Counted on the way into My Work, at most once a
  // minute as they move around, and whenever something they did changed it.
  const [work, setWork] = useState<Counts | null>(null);
  const counted = useRef(0);
  const recount = useCallback(() => { counted.current = Date.now(); myWork.count().then(setWork, () => {}); }, []);
  useEffect(() => { if (loc.pathname.startsWith("/my-work") || Date.now() - counted.current > 60_000) recount(); }, [loc.pathname, recount]);
  useEffect(() => { window.addEventListener("courage:my-work", recount); return () => window.removeEventListener("courage:my-work", recount); }, [recount]);
  // The navigation row follows the area of the page you're on, and stays on the last area for shared pages.
  const [lastArea, setLastArea] = useState<string | null>(null);
  const here = areaForPath(loc.pathname);
  useEffect(() => { setLauncher(false); setPicker(false); if (here) setLastArea(here.id); }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  // A table (/p/…) stays in the area it was opened from; opened straight from a link, it's in Productivity.
  const area = loc.pathname === "/" || loc.pathname.startsWith("/my-work") ? null : here ?? SUITE.find((d) => d.id === (new URLSearchParams(loc.search).get("a") ?? lastArea)) ?? (loc.pathname.startsWith("/p/") ? SUITE.find((d) => d.id === "productivity") ?? null : null);

  const initials = name.split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const bcEnv = (() => { try { return localStorage.getItem("bc.environment"); } catch { return null; } })();
  const bcCompany = (() => { try { return localStorage.getItem("bc.company"); } catch { return null; } })();

  return (
    <>
    {access?.viewAs && <ViewAsBanner m={access} />}
    <div className={`frame ${area ? "" : "bare"}`}>
      <header className="topbar">
        <button className="waffle" aria-label="All apps" aria-expanded={launcher} onClick={() => setLauncher((v) => !v)}>
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><g fill="currentColor">{[1, 6.5, 12].flatMap((y) => [1, 6.5, 12].map((x) => <rect key={`${x}-${y}`} x={x} y={y} width="3" height="3" />))}</g></svg>
        </button>
        <Link to="/" className="brand" aria-label="courageMFG home"><Mark /><span>courageMFG</span></Link>
        {bcCompany && <span className="company">{bcCompany}</span>}
        {bcEnv && bcEnv !== "Production" && <span className="env-tag">{bcEnv}</span>}
        {loc.pathname === "/search" ? <div className="topsearch" /> : <TopSearch />}
        <Link to="/search" className="top-btn top-search-m" aria-label="Search for a page or record"><Icon name="search" size={14} /></Link>
        <NavLink to="/my-work" className="top-btn mw-top" aria-label={work && work.waiting > 0 ? `My Work, ${work.waiting} waiting on you` : "My Work"} title={work ? `${work.waiting} waiting on you${work.asked ? `, ${work.asked} asked of you` : ""}` : "My Work"}>
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 4.5h12v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-8zm0 0 1.5-2.5h9L14 4.5M2 8h3.5l1 1.5h3l1-1.5H14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>
          <span className="lbl">My Work</span>
          {work && work.waiting > 0 && <span className="mw-badge" aria-label={`${work.waiting} waiting on you`}>{work.waiting > 99 ? "99+" : work.waiting}</span>}
        </NavLink>
        <button className="top-btn" aria-label="Courage Chat" title="Courage Chat" aria-pressed={chatOpen} onClick={() => { setChatOpen((v) => !v); try { localStorage.setItem("copilot", chatOpen ? "off" : "on"); } catch {} }}>
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1l1.6 4.4L14 7l-4.4 1.6L8 13l-1.6-4.4L2 7l4.4-1.6z" fill="currentColor" /></svg><span className="lbl">Courage Chat</span>
        </button>
        {access?.realAdmin && !access.viewAs && (
          <button className="top-btn va-toggle" aria-label="View as" aria-expanded={picker} onClick={() => setPicker((v) => !v)} title="See courageMFG as someone else">
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3C4 3 1.5 8 1.5 8S4 13 8 13s6.5-5 6.5-5S12 3 8 3zm0 8a3 3 0 110-6 3 3 0 010 6z" fill="currentColor" /></svg><span className="lbl">View as</span>
          </button>
        )}
        {picker && <ViewAsPicker onClose={() => setPicker(false)} />}
        <button className="avatar" title={`${name}: sign out`} aria-label={`${name}, sign out`} onClick={() => config.authMode !== "dev" && signOut()}>{initials}</button>
      </header>

      {area && <AreaNav area={area} open={open} counts={counts} />}

      <main className="main">{children}</main>

      {chatOpen && <CourageChat onClose={() => setChatOpen(false)} />}
      {launcher && <Launcher onClose={() => setLauncher(false)} waiting={waitingByArea(work)} first={startArea(access)?.id ?? null} />}

    </div>
    </>
  );
}

// Page action bar (Business Central style)
export function CommandBar({ children, status }: { children?: ReactNode; status?: ReactNode }) {
  return (
    <div className="cmdbar" role="toolbar">
      {children}
      <span className="spacer" />
      {status && <span className="status">{status}</span>}
    </div>
  );
}
